import { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@material-ui/core";
import { History } from "history";
import { concat } from "lodash";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { QueryClient, QueryClientProvider as QCP } from "react-query";
import { BrowserRouter } from "react-router-dom";

import { ErrorBoundary } from "components";
import { NotificationModal } from "containers";
import { breakpoints } from "containers/Responsive";
import { AppContext, AppContextProps } from "contexts";
import withTheme from "hocs/withTheme";
import { getDomainNames, getNotificationMessages } from "services/app";
import { getCSRFToken, getCurrentUser } from "services/users";
import { User } from "services/users/types";
import theme from "theme";
import { desktopHeaderHeight, mobileHeaderHeight } from "theme/constants";

import { HijackSnackbar, PostHogConsentManager } from "./components";
import Routes from "./Routes";

// TODO remove when we upgrade react-query in RXT-3311
// This is for type compatibility with React v.18
const QueryClientProvider = QCP as FC<
  PropsWithChildren<{ client: QueryClient }>
>;
export const queryClient = new QueryClient();

if (typeof window !== "undefined" && !import.meta.env.REACT_APP_IS_BUILD_CI) {
  posthog.init(import.meta.env.REACT_APP_POSTHOG_APP_ID || "", {
    api_host: "https://us.i.posthog.com",
    cookieless_mode: "on_reject",
    defaults: "2025-05-24",
    person_profiles: "always",
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
  });
}

interface RootProps {
  history?: History;
}

const Root: FC<RootProps> = () => {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [domainNames, setDomainNames] = useState(null);
  const [notificationMessages, setNotificationMessages] = useState([]);

  const handleSetUser = (user: User) =>
    user && user.userId ? setUser(user) : setUser(null);

  const isDesktop = useMediaQuery(`(min-width:${breakpoints.desktop}px)`);
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const [navHeight, setNavHeight] = useState(
    isMdUp ? desktopHeaderHeight : mobileHeaderHeight,
  );

  const initialAppState = useMemo(
    () => ({
      user,
      setUser: handleSetUser,
      navHeight,
      setNavHeight,
      domainNames,
      notificationMessages,
      setNotificationMessages,
    }),
    [domainNames, navHeight, notificationMessages, user],
  );

  const appContext = useMemo<AppContextProps>(
    () => ({
      ...initialAppState,
      screenType: isDesktop ? "desktop" : "mobile",
    }),
    [initialAppState, isDesktop],
  );

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const user = await getCurrentUser();
        current && handleSetUser(user);
      } catch (e) {
        current && handleSetUser(null);
      }
    })();
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        if (domainNames == null) {
          const response = await getDomainNames();
          current && setDomainNames(response);
        }
      } catch (e) {
        current && setDomainNames(null);
      }
    })();
    return () => {
      current = false;
    };
  }, [domainNames]);

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const response = await getNotificationMessages();
        current && setNotificationMessages(m => concat(m, response));
      } catch (e) {}
    })();
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    getCSRFToken();
  }, []);

  useEffect(() => {
    posthog?.register({
      isHijacked: user?.isHijacked,
    });
  }, [user?.isHijacked]);

  return (
    // we want ErrorBoundary at the very top so we violate the "only use posthog instance from hook" rule in this one place
    // https://posthog.com/docs/libraries/react
    <ErrorBoundary posthog={posthog}>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <NotificationModal />
            <AppContext.Provider value={appContext}>
              {user !== undefined && domainNames && (
                <>
                  <PostHogConsentManager />
                  <Routes />
                  {/* Only show React hijack banner in development - staging/prod uses django-hijack native notification */}
                  {import.meta.env.DEV && user && user.isHijacked && (
                    <HijackSnackbar user={user} />
                  )}
                </>
              )}
            </AppContext.Provider>
          </BrowserRouter>
        </QueryClientProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
};

export default withTheme(Root);
